/** Подписи статусов лояльности для отображения в UI */
export const LOYALTY_STATUS_LABELS = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

/** Карточки tier для блоков статистики (platinum → bronze) */
export const LOYALTY_TIER_CARDS = [
  { key: 'platinum', label: 'Платиновые' },
  { key: 'gold', label: 'Золотые' },
  { key: 'silver', label: 'Серебряные' },
  { key: 'bronze', label: 'Бронзовые' },
]

/** Форматирует код статуса (bronze/gold/…) в читаемую подпись */
export function formatLoyaltyStatus(value) {
  if (value == null || value === '') return '—'
  const key = String(value).toLowerCase()
  if (LOYALTY_STATUS_LABELS[key]) return LOYALTY_STATUS_LABELS[key]
  return String(value).charAt(0).toUpperCase() + String(value).slice(1).toLowerCase()
}

/**
 * Извлекает статус из ответа GET /club_player/loyalty_status.
 * fallback — например loyalty_status из списка users_points.
 */
export function extractLoyaltyStatus(data, fallback = null) {
  if (!data) return fallback ?? null
  return data.loyalty_status
    ?? data.after?.loyalty_status
    ?? data.status
    ?? data.after?.status
    ?? fallback
    ?? null
}

/** Подпись квартала: 1 → «1 квартал» */
export function formatQuarterLabel(quarter) {
  const q = Number(quarter)
  if (!Number.isFinite(q) || q < 1 || q > 4) return '—'
  return `${q} квартал`
}

/** Календарные границы квартала (ISO YYYY-MM-DD) */
export function getQuarterDateRange(year, quarter) {
  const y = Number(year)
  const q = Number(quarter)
  if (!Number.isFinite(y) || !Number.isFinite(q) || q < 1 || q > 4) {
    return { date_from: null, date_to: null }
  }
  const startMonth = (q - 1) * 3
  const endMonth = startMonth + 2
  const dateFrom = `${y}-${String(startMonth + 1).padStart(2, '0')}-01`
  const lastDay = new Date(y, endMonth + 1, 0).getDate()
  const dateTo = `${y}-${String(endMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return { date_from: dateFrom, date_to: dateTo }
}

function formatIsoDateRu(isoDate) {
  if (!isoDate) return ''
  const part = String(isoDate).trim().slice(0, 10)
  const [y, m, d] = part.split('-')
  if (!y || !m || !d) return part
  return `${d}.${m}.${y}`
}

/** Текст периода квартала для колонки «Период» */
export function formatQuarterPeriod(year, quarter, item = {}) {
  if (item.period_label) return item.period_label
  if (item.date_from && item.date_to) {
    return `${formatIsoDateRu(item.date_from)} — ${formatIsoDateRu(item.date_to)}`
  }
  const { date_from, date_to } = getQuarterDateRange(year, quarter)
  if (date_from && date_to) {
    return `${formatIsoDateRu(date_from)} — ${formatIsoDateRu(date_to)}`
  }
  return '—'
}

/**
 * Формат «Квартал-основание»: число 1–4 → «N квартал», даты → «dd.mm.yyyy — dd.mm.yyyy»
 */
export function formatBasisPeriodLabel(value) {
  if (value == null || value === '') return '—'
  const str = String(value).trim()
  const asNum = Number(str)
  if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 4) {
    return formatQuarterLabel(asNum)
  }
  const dateParts = str.split(/[,;]\s*/).map(s => s.trim()).filter(Boolean)
  if (dateParts.length >= 2) {
    return `${formatIsoDateRu(dateParts[0])} — ${formatIsoDateRu(dateParts[1])}`
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return formatIsoDateRu(str)
  }
  return str
}

/**
 * Блок loyalty_tiers_by_period из GET /club_player/statistics.
 */
export function getPeriodLoyaltyBlock(stats) {
  if (!stats) return null
  return stats.loyalty_tiers_by_period || null
}

/** Счётчики участников по tier из loyalty_tiers_by_period */
export function getPeriodLoyaltyCounts(block) {
  if (!block) {
    return { platinum: 0, gold: 0, silver: 0, bronze: 0 }
  }
  return {
    platinum: Number(block.platinum) || 0,
    gold: Number(block.gold) || 0,
    silver: Number(block.silver) || 0,
    bronze: Number(block.bronze) || 0,
  }
}

function defaultPenaltySection() {
  return {
    total: 0,
    half_12_24: 0,
    full_under_12: 0,
    full_rescheduled: 0,
  }
}

function normalizeGiftList(raw) {
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') return Object.values(raw)
  return []
}

/** Нормализует ответ GET /club_player/statistics для безопасного рендера в UI */
export function normalizeStatisticsResponse(data) {
  if (!data || typeof data !== 'object') return null

  const gifts = data.gifts || {}
  const penalties = data.penalties || {}
  const freeCancelings = data.free_cancelings || {}

  return {
    ...data,
    loyalty_tiers: {
      platinum: Number(data.loyalty_tiers?.platinum) || 0,
      gold: Number(data.loyalty_tiers?.gold) || 0,
      silver: Number(data.loyalty_tiers?.silver) || 0,
      bronze: Number(data.loyalty_tiers?.bronze) || 0,
    },
    gifts: {
      total: Number(gifts.total) || 0,
      physical: normalizeGiftList(gifts.physical),
      virtual: normalizeGiftList(gifts.virtual),
    },
    free_cancelings: {
      activity: Number(freeCancelings.activity) || 0,
      court_rent: Number(freeCancelings.court_rent) || 0,
      total: Number(freeCancelings.total) || 0,
    },
    penalties: {
      available_from: penalties.available_from ?? null,
      activity: {
        ...defaultPenaltySection(),
        ...(penalties.activity || {}),
      },
      court_rent: {
        ...defaultPenaltySection(),
        ...(penalties.court_rent || {}),
      },
    },
  }
}

/**
 * Список годов для селектора истории.
 */
export function buildYearOptions(yearsBack = 15, minYear = null) {
  const current = new Date().getFullYear()
  const start = minYear != null
    ? Math.min(current, Math.max(minYear, current - yearsBack))
    : current - yearsBack
  const items = []
  for (let y = current; y >= start; y -= 1) {
    items.push({ text: String(y), value: y })
  }
  return items
}

function emptyQuarterRow(quarter, year = null) {
  const row = {
    quarter,
    period_label: '',
    loyalty_status: null,
    loyalty_status_label: '—',
    basis_points: null,
    basis_period: null,
    date_from: null,
    date_to: null,
  }
  if (year != null && quarter != null) {
    const range = getQuarterDateRange(year, quarter)
    row.date_from = range.date_from
    row.date_to = range.date_to
    row.period_label = formatQuarterPeriod(year, quarter, row)
  }
  return row
}

function normalizeYearlyStatusItem(item, defaultQuarter = null) {
  if (!item || typeof item !== 'object') {
    return defaultQuarter != null ? emptyQuarterRow(defaultQuarter) : null
  }
  const status = item.loyalty_status ?? item.status ?? null
  const basisPeriod = item.basis_period ?? item.basis_quarter ?? null
  const quarter = item.quarter ?? item.q ?? defaultQuarter ?? null
  return {
    quarter,
    period_label: item.period_label ?? item.label ?? item.period ?? '',
    loyalty_status: status,
    loyalty_status_label: formatLoyaltyStatus(status),
    basis_points: item.basis_points != null ? Number(item.basis_points) : null,
    basis_period: basisPeriod != null ? String(basisPeriod) : null,
    date_from: item.date_from ?? null,
    date_to: item.date_to ?? null,
  }
}

function collectQuartersFromObject(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {}
  const byQuarter = {}
  for (let q = 1; q <= 4; q += 1) {
    const candidate = obj[`q${q}`] ?? obj[`quarter_${q}`] ?? obj[String(q)]
    if (candidate == null) continue
    byQuarter[q] = normalizeYearlyStatusItem(
      typeof candidate === 'object'
        ? { ...candidate, quarter: candidate.quarter ?? candidate.q ?? q }
        : { quarter: q, loyalty_status: candidate },
      q,
    )
  }
  return byQuarter
}

function applyYearToQuarterRows(items, year) {
  if (year == null) return items
  return items.map(item => {
    if (!item || item.quarter == null) return item
    const range = getQuarterDateRange(year, item.quarter)
    const withDates = {
      ...item,
      date_from: item.date_from || range.date_from,
      date_to: item.date_to || range.date_to,
    }
    return {
      ...withDates,
      period_label: formatQuarterPeriod(year, item.quarter, withDates),
    }
  })
}

function ensureFourQuarters(items, year = null) {
  const byQ = {}
  ;(items || []).forEach(item => {
    if (item && item.quarter != null) {
      byQ[item.quarter] = item
    }
  })
  const rows = [1, 2, 3, 4].map(q => byQ[q] || emptyQuarterRow(q, year))
  return applyYearToQuarterRows(rows, year)
}

/**
 * Парсит ответ GET /club_player/loyalty_yearly_statuses.
 * year — выбранный год (для вычисления колонки «Период»).
 */
export function parseLoyaltyYearlyStatuses(data, year = null) {
  const resolvedYear = year ?? (data && typeof data === 'object' && !Array.isArray(data) ? data.year : null)
  const y = resolvedYear != null ? Number(resolvedYear) : null
  const effectiveYear = y != null ? y : (data?.year != null ? Number(data.year) : null)

  if (!data) {
    return ensureFourQuarters([], effectiveYear)
  }

  if (Array.isArray(data)) {
    return ensureFourQuarters(
      data.map(item => normalizeYearlyStatusItem(item)).filter(Boolean),
      effectiveYear,
    )
  }

  if (typeof data !== 'object') {
    return ensureFourQuarters([], effectiveYear)
  }

  const raw = data.quarters
    ?? data.yearly_statuses
    ?? data.history
    ?? data.results
    ?? data.statuses
    ?? null

  if (Array.isArray(raw)) {
    return ensureFourQuarters(
      raw.map(item => normalizeYearlyStatusItem(item)).filter(Boolean),
      effectiveYear,
    )
  }

  const fromNested = raw && typeof raw === 'object' ? collectQuartersFromObject(raw) : {}
  const fromRoot = collectQuartersFromObject(data)
  const merged = []
  for (let q = 1; q <= 4; q += 1) {
    merged.push(fromRoot[q] || fromNested[q] || null)
  }
  if (merged.some(Boolean)) {
    return ensureFourQuarters(merged.filter(Boolean), effectiveYear)
  }

  return ensureFourQuarters([], effectiveYear)
}

/** Календарные границы квартала относительно текущей даты (ISO YYYY-MM-DD) */
export function getQuarterDates(offsetQuarters = 0) {
  const now = new Date()
  const year = now.getFullYear()
  const q = Math.floor(now.getMonth() / 3) + offsetQuarters

  const absQ = ((q % 4) + 4) % 4
  const yearOffset = Math.floor(q / 4)
  const actualYear = year + yearOffset

  const startMonth = absQ * 3
  const endMonth = startMonth + 2

  const start = new Date(actualYear, startMonth, 1)
  const end = new Date(actualYear, endMonth + 1, 0)

  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  }
}

/** Минимальный год из ISO-дат в history (для расширения селектора года) */
export function minYearFromDateStrings(dates = []) {
  let min = null
  dates.forEach(d => {
    if (!d) return
    const y = parseInt(String(d).slice(0, 4), 10)
    if (Number.isFinite(y) && (min == null || y < min)) {
      min = y
    }
  })
  return min
}
